import mitt, { type Emitter } from 'mitt'

import { type SupportDialogName } from '~/components/dialogs/DialogSupport.vue'
import { type UserFilters } from '~/components/Users.vue'
import { type CaseFilterFilters } from '~/composables/useCases.ts'
import { type Batch, type Label, type Report } from '~/types.ts'

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
export type Events = {
  'alerts.fetch': undefined
  'batch.created': Batch
  'batch.finished': string
  'caseAudits.fetch': undefined
  'cases.fetch': undefined
  'cases.updateFilters': { updates: Partial<CaseFilterFilters>; reset?: boolean; show?: boolean }
  'clients.updateFilters': { updates: Partial<ClientFilterFilters>; reset?: boolean; show?: boolean }
  'clientNotes.fetch': undefined
  'hits.resolved': undefined
  'notifications.show': undefined
  'notifications.readAll': undefined
  'dialogSupport.show': undefined | SupportDialogName
  'upgrade.show': undefined
  'users.filter': Partial<UserFilters>
  'OnboardingFormQuestionCommentButton.show': { questionId: number; commentId?: number }
  'labels.removed': number
  'labels.updated': Label
  notify: undefined
  dataDirectoryEntriesUpdated: undefined
  reportDownloaded: Report
  transactionCreated: undefined
}

/**
 * Call the callback until it returns truthy.
 */
const until = <T extends keyof Events>(event: T, callback: (params: Events[T]) => unknown) => {
  const internalCallback = (params: Events[T]) => {
    if (callback(params)) {
      bus.off(event, internalCallback)
    }
  }

  bus.on(event, internalCallback)
}

/**
 * The callback is only called once.
 */
const once = <T extends keyof Events>(event: T, callback: (params: Events[T]) => void) => {
  const internalCallback = (params: Events[T]) => {
    callback(params)
    bus.off(event, internalCallback)
  }

  bus.on(event, internalCallback)
}

export type Bus = Emitter<Events> & { once: typeof once; until: typeof until }

/**
 * Event bus for non-Vue contexts (stores, helpers, services).
 * For Vue components, use the `useBus()` composable instead for automatic cleanup.
 */
export const bus: Bus = { ...mitt<Events>(), once, until }
